import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ProjectDagDraftView from './ProjectDag/ProjectDagDraftView';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

const primaryColor = '#5757b0';

const FlowCreatorHome = ({
  onUseTemplate,
  onStart,
  onSave,
  title,
  onTitleChange,
  isStarting = false,
  error = '',
  notice = '',
  pendingInteraction = '',
  focusTitleSignal = 0,
}) => {
  const { t } = useTranslation();
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (!focusTitleSignal) return undefined;

    const focusTimer = setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);

    return () => clearTimeout(focusTimer);
  }, [focusTitleSignal]);

  const interactionLabels = {
    action: t('addcase.flow_creator_action', 'an action'),
    connector: t('addcase.flow_creator_connector', 'a connector'),
    flow: t('addcase.flow_creator_flow', 'a flow'),
  };
  const pendingInteractionLabel = interactionLabels[pendingInteraction] || '';
  const hasTitle = Boolean(String(title || '').trim());
  const primaryButtonLabel = pendingInteractionLabel
    ? t('addcase.flow_creator_continue_with', 'Continue with {{item}}', {
      item: pendingInteractionLabel,
    })
    : t('addcase.flow_creator_save_open', 'Save & open DAG');

  return (
    <Box className="container" sx={{ minHeight: '100vh', background: '#f7f8fc' }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          background: '#fff',
          padding: { xs: 1.5, md: 2 },
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible',
          mb: 1,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(150px, 1fr) minmax(320px, 640px) minmax(250px, 1fr)' },
              alignItems: 'center',
              gap: { xs: 1.5, md: 2.5 },
              px: { xs: 1.25, md: 0 },
              pb: { xs: 1, md: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{ color: '#777789', fontWeight: 700, letterSpacing: '0.08em', lineHeight: 1.2 }}
                >
                  {t('addcase.flow_creator_new_project', 'New project')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.35 }}>
                  <Chip
                    size="small"
                    label={t('addcase.flow_creator_draft', 'Draft')}
                    sx={{
                      height: 22,
                      color: primaryColor,
                      backgroundColor: 'rgba(87, 87, 176, 0.1)',
                      fontWeight: 700,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#8b8b9d', whiteSpace: 'nowrap' }}>
                    {t('addcase.flow_creator_not_saved', 'Not saved yet')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <TextField
              data-draft-tutorial-target="title"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onSave();
                }
              }}
              placeholder={t('addcase.flow_creator_title_placeholder', 'Project title')}
              label={t('addcase.flow_creator_title_label', 'Project title')}
              disabled={isStarting}
              inputRef={titleInputRef}
              autoFocus
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: <EditIcon fontSize="small" color="primary" />,
              }}
              sx={{
                maxWidth: { xs: '100%', md: '640px' },
                justifySelf: 'center',
                '& .MuiInputBase-root': {
                  fontSize: { xs: '1.4rem', sm: '1.7rem', md: '1.9rem' },
                  fontWeight: 600,
                  textAlign: 'center',
                },
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                },
                '& .MuiInputLabel-root': {
                  left: '50%',
                  transform: 'translateX(-50%) scale(1)',
                  transformOrigin: 'center',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translateX(-50%) translateY(-1.5px) scale(0.75)',
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 0.75,
              }}
            >
              <Tooltip title={primaryButtonLabel} arrow placement="bottom">
                <span>
                  <IconButton
                    aria-label={primaryButtonLabel}
                    onClick={onSave}
                    disabled={isStarting}
                    data-draft-tutorial-target="save"
                    size="small"
                    sx={{
                      width: 38,
                      height: 38,
                      color: '#fff',
                      backgroundColor: primaryColor,
                      '&:hover': { backgroundColor: '#4545a0' },
                    }}
                  >
                    {pendingInteractionLabel
                      ? <ArrowForwardRoundedIcon fontSize="small" />
                      : <SaveRoundedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: { xs: 'center', md: 'center' },
              color: hasTitle ? '#777789' : primaryColor,
              mt: 1.25,
              px: { xs: 1, md: 0 },
            }}
          >
            {hasTitle
              ? t(
                'addcase.flow_creator_autosave_hint',
                'Add your first connector or action below. The project will be created and saved automatically.'
              )
              : t(
                'addcase.flow_creator_title_hint',
                'Name your project first. You can then start building from the canvas below.'
              )}
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth={false} sx={{ px: { xs: 1, md: 1.5 }, pb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid rgba(0,0,0,0.06)',
            background: '#fff',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mx: { xs: 2, md: 5 }, mt: 2 }}>
              {error}
            </Alert>
          )}

          {notice && !error && (
            <Alert
              severity="info"
              sx={{
                mx: { xs: 1, md: 2 },
                mt: 1,
                py: 0.25,
                minHeight: 0,
                borderRadius: 1.5,
                '& .MuiAlert-icon': { py: 0.35, mr: 0.75, fontSize: 18 },
                '& .MuiAlert-message': { py: 0.35, fontSize: '0.78rem', lineHeight: 1.3 },
              }}
            >
              {notice}
            </Alert>
          )}

          {isStarting && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} sx={{ color: primaryColor }} />
              <Typography variant="body2" color="text.secondary">
                {t('addcase.flow_creator_starting', 'Saving your project...')}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 0.75,
              px: { xs: 1.5, sm: 2, md: 3 },
              py: 0.9,
              borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
              backgroundColor: '#fbfbff',
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#45459a', fontWeight: 800 }}>
                {t('addcase.flow_creator_canvas_title', 'Build your flow')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#777789' }}>
                {t('addcase.flow_creator_canvas_hint', 'Start with Main Flow, then add the pieces your project needs.')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#777789' }}>
                {pendingInteractionLabel
                  ? t('addcase.flow_creator_pending_hint', 'Your selected {{item}} will open after the project is created.', {
                    item: pendingInteractionLabel,
                  })
                  : t('addcase.flow_creator_canvas_status', 'Changes are saved automatically after creation.')}
              </Typography>
              <Button
                onClick={onUseTemplate}
                disabled={isStarting}
                size="small"
                startIcon={<ViewQuiltOutlinedIcon fontSize="small" />}
                sx={{
                  color: primaryColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.08)' },
                  '&:focus-visible': { outline: `2px solid ${primaryColor}`, outlineOffset: 2 },
                }}
              >
                {t('addcase.flow_creator_template_button', 'Use a template')}
              </Button>
            </Box>
          </Box>

          <ProjectDagDraftView onStart={onStart} />
        </Paper>
      </Container>
    </Box>
  );
};

export default FlowCreatorHome;
